import { useClerk, useUser } from "@clerk/clerk-react";
import { Loader2 } from "lucide-react";
import React, { useMemo, useState } from "react";
import { toast } from "sonner";

import { toastError, presentMutationError } from "../../../../utils/showApiErrorToast";
import {
  BROKER_ALERT_CLASS,
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
  BrokerAlertIcon,
} from "@/components/Broker/brokerAlertUi";
import { accountSettingsFieldDescriptionClassName } from "../accountSettingsCardShell";
import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { DashboardLayoutDialogFooter } from "@/views/Dashboard/Dashboard/components/DashboardLayoutDialogFooter";
import { fetchMyDataExport } from "../../../../services/meApi";
import SettingCard, {
  accountSettingsFieldClassName,
  accountSettingsFieldRowClassName,
} from "./SettingCard";

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function isReverificationError(error: unknown): boolean {
  const err = error as {
    errors?: Array<{ code?: string; message?: string }>;
    message?: string;
  };
  const code = err?.errors?.[0]?.code ?? "";
  const message = (err?.errors?.[0]?.message || err?.message || "").toLowerCase();
  return (
    code === "reverification_required" ||
    code === "session_reverification_required" ||
    message.includes("reverification")
  );
}

const AccountDataCard: React.FC = () => {
  const { user } = useUser();
  const clerk = useClerk();

  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const userEmail = user?.primaryEmailAddress?.emailAddress?.trim() ?? "";
  const deleteConfirmMatches = useMemo(
    () => Boolean(userEmail) && deleteConfirm.trim() === userEmail,
    [deleteConfirm, userEmail]
  );

  const handleExportData = async () => {
    if (!user?.id) {
      toastError("Authentication required", { description: "Please sign in to export your data." });
      return;
    }

    setIsExporting(true);
    try {
      const { blob, filename } = await fetchMyDataExport();
      triggerBlobDownload(blob, filename);
      toast.success("Export ready", {
        description: "Your data download has started.",
      });
    } catch (error: unknown) {
      presentMutationError(error, "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.id || !deleteConfirmMatches) return;

    setIsDeleting(true);
    setIsDeleteOpen(false);

    try {
      await user.delete();
      await clerk.signOut();
      window.location.assign("/auth/signin");
    } catch (error: unknown) {
      if (isReverificationError(error)) {
        toast.info("Verify your identity", {
          description: "Sign in again to confirm account deletion, then retry.",
        });
        await clerk.signOut();
        window.location.assign("/auth/signin");
        return;
      }
      presentMutationError(error, "Could not delete account");
    } finally {
      setIsDeleting(false);
      setDeleteConfirm("");
    }
  };

  return (
    <>
      <SettingCard
        title="Your data & account"
        description="Download a copy of your Tessera data or permanently delete your account. Download includes your account-linked data stored on Tessera. Deleting your account removes your Clerk profile and triggers backend erasure of your data."
      >
        <div className={accountSettingsFieldRowClassName}>
            <Button
              type="button"
              variant="outline-brand"
              size="sm"
              data-font="primary"
              className="shrink-0"
              disabled={isExporting}
              aria-busy={isExporting || undefined}
              onClick={() => void handleExportData()}
            >
              {isExporting ? (
                <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
              ) : null}
              Download my data
            </Button>

            <Button
              type="button"
              variant="outline-red"
              size="sm"
              data-font="primary"
              className="shrink-0"
              disabled={isDeleting}
              aria-busy={isDeleting || undefined}
              onClick={() => setIsDeleteOpen(true)}
            >
              {isDeleting ? (
                <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
              ) : null}
              Delete account
            </Button>
          </div>
      </SettingCard>

      <StrategyPineDetailDialog
        open={isDeleteOpen}
        onOpenChange={(open) => {
          if (!open && !isDeleting) {
            setIsDeleteOpen(false);
            setDeleteConfirm("");
          }
        }}
        title="Delete your account?"
        showHeader={false}
        showDismissButton={false}
        contentClassName="sm:max-w-md"
        bodyClassName="flex-none overflow-visible"
        footer={
          <DashboardLayoutDialogFooter
            onCancel={() => {
              setIsDeleteOpen(false);
              setDeleteConfirm("");
            }}
          >
            <Button
              type="button"
              variant="outline-red"
              size="sm"
              data-font="primary"
              disabled={!deleteConfirmMatches || isDeleting}
              aria-busy={isDeleting || undefined}
              onClick={() => void handleDeleteAccount()}
            >
              {isDeleting ? (
                <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
              ) : null}
              Delete account
            </Button>
          </DashboardLayoutDialogFooter>
        }
      >
        <div className="flex flex-col gap-4">
          <Alert variant="destructive" className={BROKER_ALERT_CLASS}>
            <BrokerAlertIcon tone="error" />
            <AlertDescription className={BROKER_ALERT_DESC_CLASS}>
              This permanently deletes your account and triggers erasure of your Tessera data. This
              cannot be undone.
            </AlertDescription>
          </Alert>
          <Field className={accountSettingsFieldClassName}>
            <FieldLabel htmlFor="delete-account-confirm" className={BROKER_ALERT_TITLE_CLASS}>
              Type your email address to confirm
            </FieldLabel>
            <FieldDescription className={accountSettingsFieldDescriptionClassName}>
              {userEmail || "—"}
            </FieldDescription>
            <Input
              id="delete-account-confirm"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="your@email.com"
              autoComplete="off"
              data-font="primary"
            />
          </Field>
        </div>
      </StrategyPineDetailDialog>
    </>
  );
};

export default AccountDataCard;
